import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import type { ScrollAnchor } from '@hm-spt/sola';

import * as api from '../api/client';
import { CatalogSidebar } from '../components/CatalogSidebar';
import { DetailDrawer } from '../components/DetailDrawer';
import { Pager } from '../components/Pager';
import { escapeHtml } from '../lib/cells';
import type { TableMetaFromServer } from '../types';
import { detailOf } from '../lib/events';

interface CatalogSearchViewData {
  query: string;
  subtitle: string;
  status: 'idle' | 'loading' | 'ok' | 'err';
}

/**
 * 🎯 CatalogSearchView — /catalog/_search 라우트
 *
 * 11개 카탈로그 테이블(섀시·CPU·GPU·NIC·HBA·HDD·SSD·메모리·PSU·CCM·LFF) 을
 * 한꺼번에 검색하는 통합 화면.
 *
 * 레이아웃은 CatalogView 와 동일 — topbar 에 검색 input, 메인이 그리드 100%.
 */
export class CatalogSearchView extends SolaView<CatalogSearchViewData> {
  private readonly sidebar = new CatalogSidebar();
  private readonly drawer = new DetailDrawer();
  private readonly pager = new Pager();

  private tables: TableMetaFromServer[] = [];
  private tableByName = new Map<string, TableMetaFromServer>();

  // 현재 페이지의 행 (서버에서 받은 그대로 — 클라이언트 슬라이스 X)
  private currentHits: api.CatalogSearchHit[] = [];
  // 전체 매칭 개수 (페이지 무관) — pager 표시용
  private currentTotal = 0;

  // 서버 사이드 페이지네이션 상태
  private pageLimit = 50;
  private pageOffset = 0;

  // 데이터 로드 완료 시그널 — ready() Promise 용
  private readyResolve: (() => void) | null = null;
  private readyPromise: Promise<void>;

  // 검색 디바운스
  private searchTimer?: ReturnType<typeof setTimeout>;

  override events = {
    input: { '@search': 'handleSearchInput' },
    click: {
      '@result-row': 'handleRowClick',
      '@clear': 'handleClearSearch',
    },
    'page:change': { '*': 'handlePageChange' },
  };

  constructor() {
    super();
    this.data = {
      query: '',
      subtitle: '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
      status: 'idle',
    };
    this.readyPromise = new Promise<void>((resolve) => {
      this.readyResolve = resolve;
    });
  }

  // ===== 라우터 프로토콜 =====

  override getTitle(): string {
    return this.data.query
      ? `"${this.data.query}" 검색 — 카탈로그 — HCLTech`
      : '전체 검색 — 카탈로그 — HCLTech';
  }

  override async ready(): Promise<void> {
    return this.readyPromise;
  }

  override captureScrollAnchor(): ScrollAnchor {
    return {
      scrollY: window.scrollY,
      extra: { query: this.data.query },
    };
  }

  override restoreScrollAnchor(anchor: ScrollAnchor): void {
    const q = anchor.extra?.query as string | undefined;
    if (typeof q === 'string' && q !== '') {
      this.data.query = q;
      this.notify('query');
      const input = this._rootElement?.querySelector(
        '.search-input',
      ) as HTMLInputElement | null;
      if (input) input.value = q;
      void this.runSearch(q);
    }
    if (anchor.scrollY != null) {
      window.scrollTo(0, anchor.scrollY);
    }
  }

  // ===== 라이프사이클 =====

  override onMount(): void {
    void this.loadTables();
  }

  override onUnmount(): void {
    super.onUnmount();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog page-catalog-search">
        ${this.sidebar}
        <section class="catalog-main">
          <header class="catalog-topbar">
            <div class="catalog-topbar-left">
              <h1 class="page-title">🔍 전체 카탈로그 검색</h1>
              <span class="page-subtitle search-subtitle" data-bind="subtitle">${escapeHtml(this.data.subtitle)}</span>
            </div>
            <div class="catalog-topbar-right">
              <div class="search-wrap search-wrap-wide">
                <input
                  class="search-input"
                  type="search"
                  placeholder="모델명·option_part·키워드 (예: DL380 Gen11, 6442Y)"
                  autocomplete="off"
                  spellcheck="false"
                  autofocus
                  data-action="search"
                  value="${escapeHtml(this.data.query)}"
                />
              </div>
              <button class="btn-ghost" data-action="clear" title="검색어 지우기">✕</button>
            </div>
          </header>

          <section class="grid-wrap">
            <div class="grid-scroll">
              <table class="grid search-grid">
                <thead>
                  <tr>
                    <th class="col-source">출처</th>
                    <th class="col-option-part">option_part</th>
                    <th class="col-extras">정보</th>
                  </tr>
                </thead>
                <tbody class="search-results-tbody"></tbody>
              </table>
              <div class="grid-empty">검색어를 입력하세요.</div>
            </div>
          </section>
          ${this.pager}
        </section>
        ${this.drawer}
      </div>
    `;
  }

  // ===== 데이터 로딩 =====

  private async loadTables(): Promise<void> {
    try {
      const data = await api.fetchTables();
      this.tables = data.tables;
      this.tableByName = new Map(this.tables.map((t) => [t.name, t]));
      this.sidebar.setTables(this.tables);
      this.sidebar.setCurrent('_search');
      this.sidebar.setApiStatus('ok', `API 연결됨 · ${this.tables.length}개 테이블`);
    } catch (err) {
      this.sidebar.setApiStatus('err', 'API 연결 실패');
      this.setStatus('err', `테이블 목록 로드 실패: ${(err as Error).message}`);
    } finally {
      this.readyResolve?.();
    }
  }

  /** 신규 검색 — 첫 페이지부터 fetch. */
  private async runSearch(q: string): Promise<void> {
    const trimmed = q.trim();
    if (trimmed.length === 0) {
      this.currentHits = [];
      this.currentTotal = 0;
      this.pageOffset = 0;
      this.setStatus(
        'idle',
        '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
      );
      this.renderPage();
      return;
    }
    return this.fetchPage(trimmed, 0, this.pageLimit);
  }

  /**
   * 서버에 한 페이지 분량 fetch.
   * - runSearch (새 검색) — offset 0
   * - handlePageChange (페이지 이동) — 새 offset/limit
   */
  private async fetchPage(
    q: string,
    offset: number,
    limit: number,
  ): Promise<void> {
    this.setStatus('loading', `"${q}" 검색 중…`);
    // 페이지 전환 중에도 이전 결과는 잠시 유지하지 않고 비움 (혼동 방지)
    this.currentHits = [];
    this.renderPage();
    try {
      const result = await api.searchAll(q, offset, limit);
      this.currentHits = result.catalog;
      this.currentTotal = result.total;
      this.pageOffset = result.offset;
      this.pageLimit = result.limit;
      if (result.total === 0) {
        this.setStatus('ok', `"${q}" — 일치하는 카탈로그 행이 없습니다.`);
      } else {
        this.setStatus('ok', `${result.total}건`);
      }
      this.renderPage();
    } catch (err) {
      this.setStatus('err', `검색 실패: ${(err as Error).message}`);
      this.currentHits = [];
      this.currentTotal = 0;
      this.renderPage();
    }
  }

  // ===== 렌더링 =====

  private setStatus(
    status: CatalogSearchViewData['status'],
    message: string,
  ): void {
    this.data.status = status;
    this.data.subtitle = message;
    const sub = this._rootElement?.querySelector('.search-subtitle');
    if (sub) {
      sub.textContent = message;
      sub.className = `page-subtitle search-subtitle status-${status}`;
    }
  }

  /**
   * 현재 페이지 행 렌더 + pager 상태 갱신.
   * (서버가 이미 페이지 단위로 잘라줘서 클라이언트 슬라이스 없음)
   */
  private renderPage(): void {
    this.pager.setState(this.currentTotal, this.pageLimit, this.pageOffset);

    const tbody = this._rootElement?.querySelector('.search-results-tbody');
    const empty = this._rootElement?.querySelector('.grid-empty');
    if (!tbody) return;

    if (this.currentHits.length === 0) {
      tbody.innerHTML = '';
      if (empty) {
        empty.classList.remove('hidden');
        empty.textContent =
          this.data.status === 'idle'
            ? '검색어를 입력하세요.'
            : this.data.status === 'loading'
              ? '검색 중…'
              : '일치하는 행이 없습니다.';
      }
      return;
    }

    if (empty) empty.classList.add('hidden');

    const rows = this.currentHits
      .map((hit, idx) => {
        const sourceLabel = this.labelForSource(hit.source);
        const extras = this.formatExtras(hit);
        return `
          <tr class="result-row" data-action="result-row" data-idx="${idx}">
            <td class="col-source">
              <span class="chip source-chip" title="${escapeHtml(hit.source)}">${escapeHtml(sourceLabel)}</span>
            </td>
            <td class="col-option-part">
              <span class="cell-mono">${escapeHtml(hit.option_part)}</span>
            </td>
            <td class="col-extras">${extras}</td>
          </tr>
        `;
      })
      .join('');

    tbody.innerHTML = rows;
  }

  /** source(테이블명) → 표시용 label. tableByName 에 없으면 source 그대로. */
  private labelForSource(source: string): string {
    return this.tableByName.get(source)?.label ?? source;
  }

  /**
   * extra_* 컬럼들을 "key value · key value" 형태 HTML 로 합성.
   * extra_ 접두사는 제거하여 보여줌. 빈 값은 skip.
   */
  private formatExtras(hit: api.CatalogSearchHit): string {
    const parts: string[] = [];
    for (const [key, val] of Object.entries(hit)) {
      if (!key.startsWith('extra_')) continue;
      if (val == null || val === '') continue;
      const shortKey = key.slice('extra_'.length);
      parts.push(
        `<span class="extra-pair"><span class="extra-key">${escapeHtml(shortKey)}</span> ${escapeHtml(val)}</span>`,
      );
    }
    if (parts.length === 0) return '<span class="cell-empty">—</span>';
    return parts.join(' <span class="extra-sep">·</span> ');
  }

  // ===== 이벤트 핸들러 =====

  handleSearchInput(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.query = q;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined;
      void this.runSearch(q);
    }, 280);
  }

  handleClearSearch(): void {
    this.data.query = '';
    const input = this._rootElement?.querySelector(
      '.search-input',
    ) as HTMLInputElement | null;
    if (input) {
      input.value = '';
      input.focus();
    }
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.currentHits = [];
    this.currentTotal = 0;
    this.pageOffset = 0;
    this.setStatus(
      'idle',
      '검색어를 입력하면 11개 카탈로그를 동시에 검색합니다.',
    );
    this.renderPage();
  }

  /**
   * 페이지 변경 시 서버에서 해당 페이지 fetch.
   * 현재 검색어 (this.data.query) 그대로 사용.
   */
  handlePageChange(e: Event): void {
    const detail = detailOf(e, 'page:change');
    const q = this.data.query.trim();
    if (!q) return;
    void this.fetchPage(q, detail.offset, detail.limit);
    // 페이지 변경 시 그리드 영역 스크롤 위로
    const scroll = this._rootElement?.querySelector('.grid-scroll');
    if (scroll) scroll.scrollTop = 0;
  }

  async handleRowClick(e: Event, target: HTMLElement): Promise<void> {
    const tr = target.closest('tr.result-row') as HTMLElement | null;
    if (!tr) return;
    const idx = Number(tr.dataset.idx);
    if (!Number.isInteger(idx)) return;
    const hit = this.currentHits[idx];
    if (!hit) return;

    const sourceLabel = this.labelForSource(hit.source);
    // 1차: 검색 결과 row 즉시 표시 (피드백)
    this.drawer.open(
      { ...hit, source: hit.source, option_part: hit.option_part },
      `${sourceLabel} — 로딩 중…`,
    );

    // 2차: 해당 카탈로그 테이블에서 full row fetch 후 갱신
    try {
      const fullRow = await api.fetchCatalogRow(hit.source, hit.option_part);
      if (fullRow) {
        this.drawer.open(fullRow, sourceLabel);
      } else {
        this.drawer.open(
          { ...hit, source: hit.source, option_part: hit.option_part },
          `${sourceLabel} — 상세 row 없음`,
        );
      }
    } catch (err) {
      this.drawer.open(
        {
          // spread 가 먼저다 — 뒤에 두면 아래 세 속성이 전부 덮여 error 가 사라진다.
          ...hit,
          source: hit.source,
          option_part: hit.option_part,
          error: (err as Error).message,
        },
        `${sourceLabel} — 로드 실패`,
      );
    }
    void e;
  }
}
